import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { FormEvent } from 'react';

import { localLabel, type SiteDetailsInput } from '../api/quotes.ts';
import { addRoom, roomsQuery } from '../api/venues.ts';
import { Button } from '../components/button.tsx';
import { Field } from '../components/field.tsx';

// CB-REQ JOB-01, JOB-02 and JOB-14 at v1.958: what a produced event shows on its quote and its job —
// its three windows, its site details, and its lines by room. The same controls on both, as the
// venue and the delivery are.

type Window = { localStart: string; localEnd: string };

export interface EventWindowsShown {
  bumpIn: Window;
  event: Window;
  bumpOut: Window;
}

export interface SiteDetailsShown {
  contactName?: string | null;
  contactPhone?: string | null;
  loadNotes?: string | null;
}

/** "Fri 13 Nov, 07:00 – 12:00", or across days, each end in full. */
function span(w: Window): string {
  const sameDay = w.localStart.slice(0, 10) === w.localEnd.slice(0, 10);
  return sameDay
    ? `${localLabel(w.localStart)} – ${w.localEnd.slice(11, 16)}`
    : `${localLabel(w.localStart)} – ${localLabel(w.localEnd)}`;
}

/** The bump-in, the event and the bump-out, as stats, where a dry hire shows out and back. */
export function EventTimes({ windows }: { windows: EventWindowsShown }) {
  return (
    <>
      {(
        [
          ['Bump-in', windows.bumpIn],
          ['Event', windows.event],
          ['Bump-out', windows.bumpOut],
        ] as const
      ).map(([label, w]) => (
        <div key={label} className="stat">
          <dt className="kicker">{label}</dt>
          <dd className="mt-1.5 font-extrabold">{span(w)}</dd>
        </div>
      ))}
    </>
  );
}

/** The site contact and the load notes, with an edit form for someone who may change them. */
export function SiteDetailsPanel({
  site,
  editable,
  saving,
  error,
  onSave,
}: {
  site: SiteDetailsShown;
  editable: boolean;
  saving: boolean;
  error: Error | null;
  onSave: (site: SiteDetailsInput) => Promise<unknown>;
}) {
  const [editing, setEditing] = useState(false);
  const [contactName, setContactName] = useState(site.contactName ?? '');
  const [contactPhone, setContactPhone] = useState(site.contactPhone ?? '');
  const [loadNotes, setLoadNotes] = useState(site.loadNotes ?? '');

  async function submit(event: FormEvent) {
    event.preventDefault();
    // Blank clears a detail; the server stores it as absent.
    await onSave({ contactName, contactPhone, loadNotes });
    setEditing(false);
  }

  const contact = [site.contactName, site.contactPhone].filter(Boolean).join(' · ');
  return (
    <section className="flex flex-col gap-2" aria-labelledby="site">
      <div className="flex items-center justify-between gap-4">
        <h2 id="site" className="section-title flex-1">
          Site contact and access
        </h2>
        {editable && !editing ? (
          <Button variant="quiet" onClick={() => setEditing(true)}>
            Change…
          </Button>
        ) : null}
      </div>
      {editing ? (
        <form onSubmit={submit} className="flex flex-col gap-3">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="Site contact"
              value={contactName}
              onChange={(e) => setContactName(e.currentTarget.value)}
            />
            <Field
              label="Their phone"
              type="tel"
              value={contactPhone}
              onChange={(e) => setContactPhone(e.currentTarget.value)}
            />
          </div>
          <Field
            label="Load and access notes"
            hint="Dock, parking, lifts, access times."
            value={loadNotes}
            onChange={(e) => setLoadNotes(e.currentTarget.value)}
          />
          <div className="flex gap-3">
            <Button type="submit" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
            <Button type="button" variant="quiet" onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </div>
        </form>
      ) : (
        <dl className="flex flex-col gap-1 text-sm">
          <div>
            <dt className="sr-only">Site contact</dt>
            <dd>{contact || <span className="text-muted">No site contact yet</span>}</dd>
          </div>
          <div>
            <dt className="sr-only">Load and access notes</dt>
            <dd className="text-ink-soft">{site.loadNotes ?? 'No load notes'}</dd>
          </div>
        </dl>
      )}
      {error ? (
        <p role="alert" className="text-sm font-semibold text-accent-2-700">
          {error.message}
        </p>
      ) : null}
    </section>
  );
}

/**
 * JOB-14 at v1.958: lines grouped under their room, rooms by name and lines with none last. Within a
 * room the lines keep their order. Grouping is how the lines are shown, not where they are kept.
 */
export function byRoom<L extends { room?: { id: string; name: string } | null }>(
  lines: readonly L[],
): Array<{ room: { id: string; name: string } | null; lines: L[] }> {
  const groups = new Map<string, { room: { id: string; name: string } | null; lines: L[] }>();
  for (const line of lines) {
    const key = line.room?.id ?? '';
    const group = groups.get(key) ?? { room: line.room ?? null, lines: [] };
    group.lines.push(line);
    groups.set(key, group);
  }
  return [...groups.values()].sort((a, b) =>
    a.room === null ? 1 : b.room === null ? -1 : a.room.name.localeCompare(b.room.name),
  );
}

/**
 * The room a line goes to: one of the venue's, none, or a new one named here — VEN-04 at v1.958 adds
 * rooms from a quote line the first time they are used.
 */
/**
 * The room a new line goes in: the one chosen, or one named in RoomPicker but not added yet, which is
 * added now (pressing add with a name typed meant "this room"). Shared by the line form and the item
 * picker so the two cannot drift.
 */
export async function roomForLine(
  queryClient: QueryClient,
  venueId: string,
  roomId: string | null,
  unaddedRoom: string | null,
): Promise<string | null> {
  if (!unaddedRoom) return roomId;
  const added = await addRoom(venueId, unaddedRoom);
  await queryClient.invalidateQueries({ queryKey: roomsQuery(venueId).queryKey });
  return added.id;
}

export function RoomPicker({
  venueId,
  value,
  onChange,
  onNaming,
}: {
  venueId: string;
  value: string | null;
  onChange: (roomId: string | null) => void;
  /**
   * A room name typed but not yet added, or null. The line form adds it on save: pressing the form's
   * own button with a name typed meant "this room", and dropping it silently put the line in none.
   */
  onNaming?: (name: string | null) => void;
}) {
  const rooms = useQuery(roomsQuery(venueId));
  const queryClient = useQueryClient();
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState('');
  const [problem, setProblem] = useState<string | undefined>();
  const add = useMutation({
    mutationFn: () => addRoom(venueId, name.trim()),
    onSuccess: async (room) => {
      await queryClient.invalidateQueries({ queryKey: roomsQuery(venueId).queryKey });
      onChange(room.id);
      setNaming(false);
      setName('');
      onNaming?.(null);
    },
  });

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor="line-room" className="text-xs text-ink-soft">
        Room
      </label>
      <select
        id="line-room"
        className="input"
        value={naming ? '+' : (value ?? '')}
        disabled={rooms.isPending}
        onChange={(e) => {
          const chosen = e.currentTarget.value;
          if (chosen === '+') {
            setNaming(true);
            return onNaming?.(name.trim() || null);
          }
          setNaming(false);
          onNaming?.(null);
          onChange(chosen || null);
        }}
      >
        <option value="">No room</option>
        {(rooms.data ?? []).map((r) => (
          <option key={r.id} value={r.id}>
            {r.name}
          </option>
        ))}
        <option value="+">+ Room</option>
      </select>
      {naming ? (
        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-48 flex-1">
            <Field
              label="New room’s name"
              value={name}
              error={problem}
              onChange={(e) => {
                setName(e.currentTarget.value);
                onNaming?.(e.currentTarget.value.trim() || null);
              }}
            />
          </div>
          <Button
            type="button"
            variant="quiet"
            disabled={add.isPending}
            onClick={() => {
              // Said, not a greyed button (CB-REQ ADO-06).
              if (!name.trim()) return setProblem('Name the room.');
              setProblem(undefined);
              add.mutate();
            }}
          >
            Add
          </Button>
        </div>
      ) : null}
      {add.error ? (
        <p role="alert" className="text-sm font-semibold text-accent-2-700">
          {add.error.message}
        </p>
      ) : null}
    </div>
  );
}
